<template>
  <div v-if="show" class="annual-overlay" @click.self="$emit('close')">
    <div class="annual-container">
      <div class="annual-header">
        <div class="header-content">
          <h2>{{ categoryName }}</h2>
          <div class="header-subtitle">
            {{ year }} Annual Detail ({{ filteredTransactions.length }}
            transactions) - Total:
            {{ formatNetAmount(totalDebit, totalCredit) }}
          </div>
        </div>
        <div class="header-buttons">
          <button class="print-button" @click="printAnnualDetail" title="Print">
            <i class="fas fa-print"></i>
          </button>
          <button
            class="refresh-button"
            @click="$emit('refresh')"
            title="Refresh data"
          >
            <i class="fas fa-sync-alt"></i>
          </button>
          <button class="close-button" @click="$emit('close')">×</button>
        </div>
      </div>

      <!-- Filter and Sort Controls -->
      <div class="controls-bar">
        <div class="filter-group">
          <i class="fas fa-filter"></i>
          <input
            type="text"
            v-model="filterText"
            placeholder="Filter transactions..."
            class="filter-input"
          />
          <button
            v-if="filterText"
            class="clear-filter"
            @click="filterText = ''"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="sort-buttons">
          <button
            class="sort-btn"
            :class="{ active: sortField === 'date' }"
            @click="toggleSort('date')"
            title="Sort by date"
          >
            <i class="fas fa-calendar-alt"></i>
            <i
              :class="
                sortField === 'date' && sortDirection === 'asc'
                  ? 'fas fa-arrow-up'
                  : 'fas fa-arrow-down'
              "
              class="sort-arrow"
            ></i>
          </button>
          <button
            class="sort-btn"
            :class="{ active: sortField === 'amount' }"
            @click="toggleSort('amount')"
            title="Sort by amount"
          >
            <i class="fas fa-dollar-sign"></i>
            <i
              :class="
                sortField === 'amount' && sortDirection === 'asc'
                  ? 'fas fa-arrow-up'
                  : 'fas fa-arrow-down'
              "
              class="sort-arrow"
            ></i>
          </button>
        </div>
      </div>

      <!-- Table -->
      <div class="table-wrapper">
        <table class="annual-table">
          <thead>
            <tr>
              <th class="col-date">Date</th>
              <th class="col-description">Description</th>
              <th class="col-class">Class</th>
              <th class="col-debit">Debit</th>
              <th class="col-credit">Credit</th>
              <th class="col-account">Account</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="tx in sortedTransactions"
              :key="tx.Id"
              class="tx-row"
              @dblclick="openTransactionInLedger(tx.Id)"
            >
              <td class="col-date">{{ formatDate(tx.BankRefDatetime) }}</td>
              <td
                class="col-description"
                :title="tx.Description || tx.BankRefDesc"
              >
                {{ tx.Description || tx.BankRefDesc }}
              </td>
              <td class="col-class" :title="tx.Class || ''">
                {{ tx.Class || '' }}
              </td>
              <td class="col-debit" :class="{ 'has-value': tx.Debit }">
                {{ formatAmount(tx.Debit) }}
              </td>
              <td class="col-credit" :class="{ 'has-value': tx.Credit }">
                {{ formatAmount(tx.Credit) }}
              </td>
              <td class="col-account">
                {{ tx.Account || 'Unassigned' }}
                <div v-if="tx.childAccounts" class="child-accounts">
                  Split: {{ tx.childAccounts }}
                </div>
              </td>
            </tr>
            <tr v-if="filteredTransactions.length === 0" class="empty-row">
              <td colspan="6" class="empty-state">
                <i class="fas fa-search"></i>
                No matching transactions
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Ledger iframe overlay -->
    <div
      v-if="iframeUrl"
      class="ledger-iframe-overlay"
      @click.self="iframeUrl = null"
    >
      <div class="ledger-iframe-container">
        <div class="ledger-iframe-title-bar">
          <span>{{ iframeTitle }}</span>
          <button class="ledger-iframe-close-btn" @click="iframeUrl = null">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="ledger-iframe-scroll-wrapper">
          <div v-if="iframeLoading" class="ledger-iframe-spinner-overlay">
            <i class="fas fa-spinner fa-spin"></i>
            <span>Loading…</span>
          </div>
          <iframe
            :src="iframeUrl"
            class="ledger-iframe"
            @load="iframeLoading = false"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AnnualCategoryDetail',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    categoryName: {
      type: String,
      default: 'Transactions',
    },
    transactions: {
      type: Array,
      default: () => [],
    },
    year: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      filterText: '',
      sortField: 'date',
      sortDirection: 'desc',
      iframeUrl: null,
      iframeTitle: '',
      iframeLoading: false,
    };
  },
  computed: {
    filteredTransactions() {
      if (!this.filterText) {
        return this.transactions;
      }
      const filter = this.filterText.toLowerCase();
      return this.transactions.filter((tx) => {
        const desc = (tx.Description || tx.BankRefDesc || '').toLowerCase();
        const account = (tx.Account || '').toLowerCase();
        const cls = (tx.Class || '').toLowerCase();
        const childAccounts = (tx.childAccounts || '').toLowerCase();
        const debit = tx.Debit ? String(tx.Debit) : '';
        const credit = tx.Credit ? String(tx.Credit) : '';
        return (
          desc.includes(filter) ||
          account.includes(filter) ||
          cls.includes(filter) ||
          childAccounts.includes(filter) ||
          debit.includes(filter) ||
          credit.includes(filter)
        );
      });
    },
    sortedTransactions() {
      const txs = [...this.filteredTransactions];
      const dir = this.sortDirection === 'asc' ? 1 : -1;

      if (this.sortField === 'date') {
        txs.sort((a, b) => {
          const dateA = new Date(a.BankRefDatetime).getTime();
          const dateB = new Date(b.BankRefDatetime).getTime();
          return (dateA - dateB) * dir;
        });
      } else if (this.sortField === 'amount') {
        txs.sort((a, b) => {
          const amtA = Math.abs(
            parseFloat(a.Debit) || parseFloat(a.Credit) || 0,
          );
          const amtB = Math.abs(
            parseFloat(b.Debit) || parseFloat(b.Credit) || 0,
          );
          return (amtA - amtB) * dir;
        });
      }
      return txs;
    },
    totalDebit() {
      return this.filteredTransactions.reduce((sum, tx) => {
        return sum + (parseFloat(tx.Debit) || 0);
      }, 0);
    },
    totalCredit() {
      return this.filteredTransactions.reduce((sum, tx) => {
        return sum + (parseFloat(tx.Credit) || 0);
      }, 0);
    },
  },
  methods: {
    toggleSort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = field === 'amount' ? 'desc' : 'desc';
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const yr = date.getFullYear().toString().slice(-2);
      return `${month}/${day}/${yr}`;
    },

    formatAmount(amount) {
      if (!amount || amount === 0) return '';
      const absAmount = Math.abs(parseFloat(amount));
      return absAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatNetAmount(debit, credit) {
      const net = (parseFloat(credit) || 0) - (parseFloat(debit) || 0);
      const absNet = Math.abs(net);
      const formatted = absNet.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      if (net < 0) {
        return `($${formatted})`;
      }
      return `$${formatted}`;
    },

    printAnnualDetail() {
      const rows = this.sortedTransactions;
      let tableRows = rows
        .map(
          (tx) => `
          <tr>
            <td>${this.formatDate(tx.BankRefDatetime)}</td>
            <td>${tx.Description || tx.BankRefDesc || ''}</td>
            <td>${tx.Class || ''}</td>
            <td style="text-align:right">${this.formatAmount(tx.Debit)}</td>
            <td style="text-align:right">${this.formatAmount(tx.Credit)}</td>
            <td>${tx.Account || 'Unassigned'}${tx.childAccounts ? ' (Split: ' + tx.childAccounts + ')' : ''}</td>
          </tr>`,
        )
        .join('');

      const totalRow = `
        <tr style="font-weight:bold; border-top:2px solid #999;">
          <td colspan="3" style="text-align:right">Total (${rows.length} transactions)</td>
          <td style="text-align:right">${this.formatAmount(this.totalDebit)}</td>
          <td style="text-align:right">${this.formatAmount(this.totalCredit)}</td>
          <td>${this.formatNetAmount(this.totalDebit, this.totalCredit)}</td>
        </tr>`;

      const printWindow = window.open('', '_blank');
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>${this.categoryName} - ${this.year} Annual Detail</title>
            <style>
              @page { size: landscape; margin: 0.5in; }
              body { font-family: Arial, sans-serif; margin: 0; padding: 10px; }
              h2 { margin: 0 0 4px 0; font-size: 1.2em; }
              .subtitle { color: #666; font-size: 0.9em; margin-bottom: 12px; }
              table { width: 100%; border-collapse: collapse; font-size: 0.8em; }
              th, td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; }
              th { background-color: #a080a8 !important; color: white; }
              tbody tr:nth-child(even) { background-color: #f2f2f2 !important; }
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body onload="window.print(); window.close();">
            <h2>${this.categoryName}</h2>
            <div class="subtitle">${this.year} Annual Detail</div>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Class</th>
                  <th style="text-align:right">Debit</th>
                  <th style="text-align:right">Credit</th>
                  <th>Account</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
                ${totalRow}
              </tbody>
            </table>
          </body>
        </html>
      `);
      printWindow.document.close();
    },

    openTransactionInLedger(transactionId) {
      const searchString = `:${transactionId}`;
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
      const currentDay = String(today.getDate()).padStart(2, '0');
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-${currentMonth}-${currentDay}`;
      this.iframeTitle = `Transaction :${transactionId}`;
      this.iframeLoading = true;
      this.iframeUrl = `/main?page=bank&q=${encodeURIComponent(searchString)}&start=${startDate}&end=${endDate}&hideNav=1`;
    },
  },
};
</script>

<style scoped>
.annual-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20001;
}

.annual-container {
  background-color: white;
  border-radius: 8px;
  width: 95%;
  max-width: 950px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.annual-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px;
  background: #a080a8;
  color: white;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.header-content {
  flex: 1;
}

.annual-header h2 {
  margin: 0 0 8px 0;
  font-size: 1.3em;
  font-weight: 600;
}

.header-subtitle {
  font-size: 0.95em;
  opacity: 0.95;
  font-weight: 400;
}

.header-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.print-button,
.refresh-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 14px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 4px;
  transition: all 0.2s;
}

.print-button:hover,
.refresh-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

.close-button {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 5px 10px;
  transition: opacity 0.2s;
  line-height: 1;
}

.close-button:hover {
  opacity: 0.8;
}

/* Controls Bar */
.controls-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 15px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  gap: 12px;
}

.filter-group {
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 400px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0 10px;
}

.filter-group i.fa-filter {
  color: #999;
  font-size: 0.85em;
}

.filter-input {
  border: none;
  outline: none;
  padding: 7px 8px;
  font-size: 0.9em;
  flex: 1;
  background: transparent;
}

.clear-filter {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 0.8em;
}

.clear-filter:hover {
  color: #666;
}

.sort-buttons {
  display: flex;
  gap: 6px;
}

.sort-btn {
  background: rgba(160, 128, 168, 0.1);
  border: 1px solid #ddd;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.sort-btn:hover {
  background: rgba(160, 128, 168, 0.2);
  border-color: #a080a8;
}

.sort-btn.active {
  background: #a080a8;
  color: white;
  border-color: #a080a8;
}

.sort-arrow {
  font-size: 10px;
}

/* Table */
.table-wrapper {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.table-wrapper::-webkit-scrollbar {
  display: none;
}

.annual-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9em;
}

.annual-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.annual-table th {
  background: #f0eaf2;
  color: #555;
  font-weight: 600;
  padding: 8px 10px;
  text-align: left;
  border-bottom: 2px solid #d0c0d4;
  font-size: 0.85em;
  white-space: nowrap;
}

.annual-table td {
  padding: 7px 10px;
  border-bottom: 1px solid #eee;
  vertical-align: top;
}

.tx-row {
  transition: background-color 0.15s;
  cursor: pointer;
}

.tx-row:hover {
  background-color: #f5f0f7;
}

.tx-row:nth-child(even) {
  background-color: #fafafa;
}

.tx-row:nth-child(even):hover {
  background-color: #f5f0f7;
}

/* Column widths */
.col-date {
  width: 80px;
  white-space: nowrap;
  color: #666;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}

.col-description {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-class {
  width: 120px;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #666;
  font-style: italic;
}

.col-debit,
.col-credit {
  width: 90px;
  text-align: right;
  font-family: 'Courier New', monospace;
  color: #999;
}

.col-debit.has-value {
  color: #d32f2f;
}

.col-credit.has-value {
  color: #2e7d32;
}

th.col-debit,
th.col-credit {
  text-align: right;
}

.col-account {
  width: 150px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.9em;
  color: #666;
}

.child-accounts {
  margin-top: 2px;
  font-size: 0.85em;
  color: #a080a8;
}

/* Empty state */
.empty-row td {
  border-bottom: none;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 1em;
}

.empty-state i {
  margin-right: 8px;
  color: #ccc;
}

/* Ledger iframe overlay */
.ledger-iframe-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.55);
  z-index: 30000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.ledger-iframe-container {
  width: 90vw;
  height: 100vh;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
}

.ledger-iframe-title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  background: #333;
  color: #eee;
  font-size: 0.95em;
  flex-shrink: 0;
}

.ledger-iframe-close-btn {
  background: #555;
  border: 1px solid #777;
  color: #eee;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7em;
}

.ledger-iframe-close-btn:hover {
  background: #d32f2f;
}

.ledger-iframe-scroll-wrapper {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.ledger-iframe-spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #1e1e2e;
  z-index: 1;
  gap: 12px;
  color: #ccc;
  font-size: 1.1em;
}

.ledger-iframe-spinner-overlay .fa-spinner {
  font-size: 2em;
  color: #4fc3f7;
}

.ledger-iframe {
  border: none;
  width: 100%;
  height: 100%;
}
</style>
